import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { useSubscription } from './SubscriptionContext';
import { 
  BusinessProfile,
  loadProfilesFromSupabase,
  saveProfileToSupabase,
  updateProfileInSupabase,
  deleteProfileFromSupabase,
  isUserAuthenticated,
  getCurrentActiveProfileId,
  setCurrentActiveProfileId
} from '@/lib/supabase-profiles-operations';
import { EnhancedSupabaseDataManager } from '@/lib/enhanced-supabase-data-management';

interface Profile {
  id: string;
  name: string;
  email: string;
  type: 'personal' | 'business';
  color: string;
  isActive: boolean;
  timezone: string;
  createdAt: string;
}

interface ProfileContextType {
  profiles: Profile[];
  activeProfile: Profile | null;
  isProfileLoading: boolean;
  setActiveProfile: (profile: Profile | null) => void;
  addProfile: (profile: Omit<Profile, 'id' | 'createdAt'>) => Promise<Profile>;
  updateProfile: (id: string, updates: Partial<Profile>) => Promise<void>;
  deleteProfile: (id: string) => Promise<void>;
  clearProfileData: (profileId: string) => Promise<void>;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [activeProfile, setActiveProfile] = useState<Profile | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);

  const { user } = useAuth();
  const { toast } = useToast();
  const { planLimits } = useSubscription();
  const dataManager = EnhancedSupabaseDataManager.getInstance();

  // Convert BusinessProfile to Profile
  const convertBusinessProfileToProfile = (bp: BusinessProfile): Profile => ({
    id: bp.id,
    name: bp.name,
    email: '', // Email não é mais usado nos perfis
    type: bp.type,
    color: bp.color,
    isActive: bp.is_active,
    timezone: bp.timezone,
    createdAt: bp.created_at
  });

  // Convert Profile to BusinessProfile data
  const convertProfileToBusinessProfileData = (profile: Omit<Profile, 'id' | 'createdAt'>): Omit<BusinessProfile, 'id' | 'user_id' | 'created_at' | 'updated_at'> => ({
    name: profile.name,
    type: profile.type,
    color: profile.color,
    is_active: profile.isActive,
    timezone: profile.timezone
  });

  // Load profiles when user authenticates
  useEffect(() => {
    const loadProfiles = async () => {
      if (!user) {
        console.log('[Profiles] No user authenticated, clearing profiles');
        setProfiles([]);
        setActiveProfile(null);
        setIsProfileLoading(false);
        return;
      }

      setIsProfileLoading(true);
      
      try {
        console.log('[Profiles] Loading profiles from Supabase...');
        const businessProfiles = await loadProfilesFromSupabase();
        const convertedProfiles = businessProfiles.map(convertBusinessProfileToProfile);
        
        setProfiles(convertedProfiles);
        
        // Set active profile
        const savedActiveProfileId = getCurrentActiveProfileId();
        let activeProfile = null;
        
        if (savedActiveProfileId) {
          activeProfile = convertedProfiles.find(p => p.id === savedActiveProfileId);
        }
        
        if (!activeProfile && convertedProfiles.length > 0) {
          // If no saved active profile or it doesn't exist, use the first one
          activeProfile = convertedProfiles.find(p => p.isActive) || convertedProfiles[0];
          if (activeProfile) {
            setCurrentActiveProfileId(activeProfile.id);
          }
        }
        
        setActiveProfile(activeProfile);
        console.log(`[Profiles] Loaded ${convertedProfiles.length} profiles, active: ${activeProfile?.name}`);
        
      } catch (error) {
        console.error('[Profiles] Error loading profiles:', error);
        toast({
          title: 'Erro ao carregar perfis',
          description: 'Não foi possível carregar seus perfis. Tente novamente.',
          variant: 'destructive'
        });
        
        // Fallback to empty state
        setProfiles([]);
        setActiveProfile(null);
      } finally {
        setIsProfileLoading(false);
      }
    };

    loadProfiles();
  }, [user, toast]);

  const handleSetActiveProfile = (profile: Profile | null) => {
    setActiveProfile(profile);
    if (profile) {
      setCurrentActiveProfileId(profile.id);
      console.log(`[Profiles] Active profile set to: ${profile.name}`);
    }
  };

  const addProfile = async (profileData: Omit<Profile, 'id' | 'createdAt'>): Promise<Profile> => {
    // Enforce subscription limits
    const max = planLimits.maxProfiles;
    if (max !== 'unlimited' && profiles.length >= max) {
      toast({
        title: 'Limite de perfis atingido',
        description: `Seu plano atual permite até ${max} perfis. Atualize seu plano para criar mais perfis.`,
        variant: 'destructive'
      });
      throw new Error('Limite de perfis atingido');
    }

    if (!await isUserAuthenticated()) {
      throw new Error('Usuário não autenticado');
    }

    try {
      console.log('[Profiles] Creating new profile:', profileData.name);
      const businessProfileData = convertProfileToBusinessProfileData(profileData);
      const savedBusinessProfile = await saveProfileToSupabase(businessProfileData);
      const newProfile = convertBusinessProfileToProfile(savedBusinessProfile);
      
      setProfiles(prev => [...prev, newProfile]);
      
      toast({
        title: 'Perfil criado',
        description: `O perfil "${newProfile.name}" foi criado com sucesso.`
      });
      
      return newProfile;
    } catch (error) {
      console.error('[Profiles] Error creating profile:', error);
      toast({
        title: 'Erro ao criar perfil',
        description: error instanceof Error ? error.message : 'Erro desconhecido',
        variant: 'destructive'
      });
      throw error;
    }
  };

  const updateProfile = async (id: string, updates: Partial<Profile>): Promise<void> => {
    if (!await isUserAuthenticated()) {
      throw new Error('Usuário não autenticado');
    }

    try {
      console.log('[Profiles] Updating profile:', id);
      
      // Convert updates to BusinessProfile format
      const businessProfileUpdates: Partial<BusinessProfile> = {};
      if (updates.name !== undefined) businessProfileUpdates.name = updates.name;
      if (updates.type !== undefined) businessProfileUpdates.type = updates.type;
      if (updates.color !== undefined) businessProfileUpdates.color = updates.color;
      if (updates.isActive !== undefined) businessProfileUpdates.is_active = updates.isActive;
      if (updates.timezone !== undefined) businessProfileUpdates.timezone = updates.timezone;
      
      await updateProfileInSupabase(id, businessProfileUpdates);
      
      setProfiles(prev => prev.map(profile => 
        profile.id === id ? { ...profile, ...updates } : profile
      ));
      
      if (activeProfile?.id === id) {
        setActiveProfile(prev => prev ? { ...prev, ...updates } : null);
      }
      
      toast({
        title: 'Perfil atualizado',
        description: 'As alterações foram salvas com sucesso.'
      });
      
    } catch (error) {
      console.error('[Profiles] Error updating profile:', error);
      toast({
        title: 'Erro ao atualizar perfil',
        description: error instanceof Error ? error.message : 'Erro desconhecido',
        variant: 'destructive'
      });
      throw error;
    }
  };

  const deleteProfile = async (id: string): Promise<void> => {
    if (!await isUserAuthenticated()) {
      throw new Error('Usuário não autenticado');
    }

    if (profiles.length <= 1) {
      throw new Error('Não é possível excluir o último perfil');
    }

    try {
      console.log('[Profiles] Deleting profile:', id);
      
      // Clear profile data using enhanced system
      await clearProfileData(id);
      
      // Delete from Supabase
      await deleteProfileFromSupabase(id);
      
      setProfiles(prev => prev.filter(profile => profile.id !== id));
      
      // If the active profile is being deleted, set another one as active
      if (activeProfile?.id === id) {
        const remainingProfiles = profiles.filter(p => p.id !== id);
        const newActiveProfile = remainingProfiles.length > 0 ? remainingProfiles[0] : null;
        handleSetActiveProfile(newActiveProfile);
      }
      
      toast({
        title: 'Perfil excluído',
        description: 'O perfil e todos os seus dados foram removidos.'
      });
      
    } catch (error) {
      console.error('[Profiles] Error deleting profile:', error);
      toast({
        title: 'Erro ao excluir perfil',
        description: error instanceof Error ? error.message : 'Erro desconhecido',
        variant: 'destructive'
      });
      throw error;
    }
  };

  const clearProfileData = async (profileId: string): Promise<void> => {
    console.log(`[Profiles] Clearing data for profile: ${profileId}`);
    
    try {
      const isAuth = await isUserAuthenticated();
      
      if (isAuth) {
        console.log(`[Profiles] User authenticated - clearing Supabase data for profile ${profileId}...`);
        await dataManager.clearAllUserDataFromSupabase(profileId);
        console.log('[Profiles] Supabase data cleared successfully');
      } else {
        console.log('[Profiles] User not authenticated - local cleanup only');
      }
      
      // Clear local data for this profile
      const keysToRemove = [
        `transactions_${profileId}`,
        `categories_${profileId}`,
        `accounts_${profileId}`,
        `cards_${profileId}`,
        `budgets_${profileId}`,
        `goals_${profileId}`,
        `futureReleases_${profileId}`,
        `installmentGroups_${profileId}`,
        `debts_${profileId}`,
        `debt_installments_${profileId}`,
        `creditCardBills_${profileId}`,
      ];

      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });

      console.log(`[Profiles] Data cleared for profile ${profileId}`);
      
    } catch (error) {
      console.error('[Profiles] Error clearing profile data:', error);
      throw error;
    }
  };

  return (
    <ProfileContext.Provider
      value={{
        profiles,
        activeProfile,
        isProfileLoading,
        setActiveProfile: handleSetActiveProfile,
        addProfile,
        updateProfile,
        deleteProfile,
        clearProfileData,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};
